import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';

/**
 * Asking before something irreversible happens.
 *
 * Replaces `window.confirm`, which had three problems worth the component. It
 * cannot be read properly: the browser truncates and the name of the thing being
 * deleted ends up in the middle of a run-on sentence. Its buttons are "OK" and
 * "Cancel", so the one that destroys your data is labelled with the word people
 * press to make a box go away. And in an installed app it is a system sheet
 * arriving from nowhere, in the browser's own voice and the browser's own
 * language — which may not be the one the app is in.
 *
 * So: the name is a heading, the consequences are their own lines, and the
 * dangerous button says the verb. **Cancel takes focus on open**, because the
 * safe choice should be the one a stray Enter lands on.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  busy,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  /** What happens. A list reads better than a sentence when there is more than one. */
  body?: ReactNode;
  confirmLabel: string;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <button
        type="button"
        aria-label={t('common.cancel')}
        onClick={onClose}
        className="absolute inset-0 bg-ink/30"
      />
      {/* A sheet on a phone and a centred card from `sm` up, which is the same
          shape every other surface in this app takes. */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        className="panel relative z-10 w-full max-w-[26rem] rounded-t p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded sm:pb-5"
      >
        <h2 id="confirm-title" className="text-section">
          {title}
        </h2>
        {body && <div className="mt-2 flex flex-col gap-1 text-body text-leaf-soft">{body}</div>}
        <div className="mt-5 flex flex-wrap gap-3">
          <Button variant="danger" loading={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
          {/* `autoFocus`, not a ref: the dialog is unmounted when closed, so it
              mounts fresh every time and this fires once, on open. */}
          <Button autoFocus variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
